import { BrandConfig, BrandTheme } from '../constants/brands/types';

/** Default Autopilotee values, the same as the `:root` block in `src/index.css`. */
export const DEFAULT_BRAND_THEME: BrandTheme = {
  primary: '#0A0A0A',
  primaryHover: '#171717',
  primaryLight: '#404040',
  onPrimary: '#FFFFFF',
  accent: '#0A0A0A',
  textPrimary: '#0A0A0A',
  textSecondary: '#6B7280',
  bg: '#FFFFFF',
  bgSecondary: '#F9FAFB',
  bgElevated: '#FFFFFF',
  border: '#E5E7EB',
  fontSans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontDisplay: "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

/** `#RRGGBB` to the `R G B` triple Tailwind needs for `rgb(var(--x) / <alpha>)`. */
export function hexToRgbTriple(hex: string): string {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map(c => c + c)
          .join('')
      : clean;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n) || full.length !== 6) return '0 0 0';
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/** The CSS custom properties a theme sets on `<html>`. */
export function brandThemeToCssVariables(theme: BrandTheme): Record<string, string> {
  return {
    '--color-black': theme.primary,
    '--color-black-rgb': hexToRgbTriple(theme.primary),
    '--color-black-hover': theme.primaryHover,
    '--color-black-light': theme.primaryLight,
    '--color-on-primary': theme.onPrimary,
    '--color-accent': theme.accent,
    '--color-text-primary': theme.textPrimary,
    '--color-text-secondary': theme.textSecondary,
    '--color-border': theme.border,
    '--color-bg': theme.bg,
    '--color-bg-secondary': theme.bgSecondary,
    '--color-bg-elevated': theme.bgElevated,
    '--font-sans': theme.fontSans,
    '--font-display': theme.fontDisplay,
  };
}

function setLink(doc: Document, rel: string, href: string, attrs: Record<string, string> = {}) {
  let link = doc.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = doc.createElement('link');
    link.rel = rel;
    doc.head.appendChild(link);
  }
  link.href = href;
  for (const [key, value] of Object.entries(attrs)) link.setAttribute(key, value);
}

function setMeta(doc: Document, name: string, content: string) {
  let meta = doc.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = doc.createElement('meta');
    meta.name = name;
    doc.head.appendChild(meta);
  }
  meta.content = content;
}

/**
 * Write a brand into the document: `data-brand` on `<html>`, the CSS variables
 * every shared component reads, the title, meta description, theme color,
 * favicon, Apple touch icon and web manifest. Safe to call more than once.
 */
export function applyBrandTheme(brand: BrandConfig, doc: Document = document): void {
  const root = doc.documentElement;
  root.dataset.brand = brand.id;

  const theme = brand.theme ?? DEFAULT_BRAND_THEME;
  for (const [name, value] of Object.entries(brandThemeToCssVariables(theme))) {
    root.style.setProperty(name, value);
  }

  doc.title = brand.siteTitle;
  setMeta(doc, 'description', brand.copy.metaDescription);
  setMeta(doc, 'theme-color', theme.primary);
  setMeta(doc, 'apple-mobile-web-app-title', brand.name);

  const faviconType = brand.assets.favicon.endsWith('.svg') ? 'image/svg+xml' : 'image/x-icon';
  setLink(doc, 'icon', brand.assets.favicon, { type: faviconType });
  if (brand.assets.faviconPng) {
    setLink(doc, 'alternate icon', brand.assets.faviconPng, { type: 'image/png' });
  }
  setLink(doc, 'apple-touch-icon', brand.assets.appleTouchIcon);
  setLink(doc, 'manifest', brand.assets.manifest);
}
